import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2 } from "lucide-react";
import { fmtINR, waLink } from "@/lib/api";

export default function ContainerDetailDialog({ container: detail, photos = [], onClose, canShare = true }) {
  const [priceOverride, setPriceOverride] = useState("");
  useEffect(() => { setPriceOverride(""); }, [detail?.id]);
  if (!detail) return null;
  const absUrl = (src) => src?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${src}` : src;
  const cPhotos = photos.filter(p => p.container_id === detail.id);
  const hasDims = detail.length_mm > 0 && detail.width_mm > 0 && detail.height_mm > 0;

  const share = () => {
    const price = priceOverride && Number(priceOverride) > 0 ? Number(priceOverride) : detail.final_price;
    const priceLine = price ? `\nPrice: ${fmtINR(price)}` : "";
    const sizeLine = detail.external_dimensions || (hasDims ? `${detail.length_mm} × ${detail.width_mm} × ${detail.height_mm} mm` : "");
    const text = `*${detail.name}*\n${detail.category}${detail.class_code ? ` · ${detail.class_code}` : ""}\n${detail.flooring_remarks || ""}${sizeLine ? `\nSize: ${sizeLine}` : ""}${priceLine}`;
    window.open(waLink(text), "_blank");
  };

  const sharePhotos = () => {
    const links = cPhotos.map((p, i) => `${i + 1}. ${absUrl(p.src)}`).join("\n");
    const text = `*${detail.name}* — Photos\n${detail.category}${detail.class_code ? ` · ${detail.class_code}` : ""}\n\n${links}`;
    window.open(waLink(text), "_blank");
  };

  const Row = ({ label, value, mono }) => value ? (
    <div className="flex justify-between gap-3"><span className="text-slate-500 shrink-0">{label}</span><span className={`font-medium text-right ${mono ? "font-mono-tab" : ""}`}>{value}</span></div>
  ) : null;

  return (
    <Dialog open={!!detail} onOpenChange={o => !o && onClose()}>
      <DialogContent data-testid="container-detail-dialog" className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base leading-snug pr-6">{detail.name}</DialogTitle></DialogHeader>
        <div className="space-y-2 text-sm">
          <Row label="Category" value={detail.category} />
          <Row label="Type / Model" value={detail.model} />
          {detail.class_code && <div className="flex justify-between"><span className="text-slate-500">Class Code</span><span className="font-mono-tab font-semibold">{detail.class_code}</span></div>}
          <Row label="Flooring" value={detail.flooring_remarks} />
          <Row label="External Size" value={detail.external_dimensions} mono />
          {!detail.external_dimensions && hasDims && <Row label="Inner Size" value={`${detail.length_mm} × ${detail.width_mm} × ${detail.height_mm} mm`} mono />}
          <Row label="Interior" value={detail.interior} />
          <Row label="Exterior" value={detail.exterior} />
          <Row label="Insulation" value={detail.insulation} />
          <Row label="Rear Door" value={detail.rear_door} />
          {detail.standard_price != null && <div className="flex justify-between pt-2 border-t"><span className="text-slate-500">Basic Price</span><span className="font-mono-tab">{fmtINR(detail.standard_price)}</span></div>}
          {detail.final_price != null && <div className="flex justify-between"><span className="text-slate-500">Final Price</span><span className="font-mono-tab font-semibold text-blue-600">{fmtINR(detail.final_price)}</span></div>}
          {detail.final_price == null && <div className="text-xs font-mono-tab uppercase tracking-widest text-slate-400 pt-2 border-t">[Pricing Restricted]</div>}
        </div>

        {cPhotos.length > 0 && (
          <div className="pt-3 border-t border-slate-100" data-testid="catalog-photos-grid">
            <div className="text-eyebrow mb-2">Photos ({cPhotos.length})</div>
            <div className="grid grid-cols-3 gap-2">
              {cPhotos.map(p => (
                <div key={p.id} className="aspect-square rounded-lg overflow-hidden bg-slate-100" data-testid={`catalog-detail-photo-${p.id}`}>
                  <img src={absUrl(p.src)} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        )}

        {canShare && detail.final_price != null && (
          <div className="pt-2 border-t border-slate-100">
            <label className="text-eyebrow block mb-1.5">Adjust price before sharing (optional)</label>
            <input type="number" value={priceOverride} onChange={e => setPriceOverride(e.target.value)}
              placeholder={String(detail.final_price)}
              className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm font-mono-tab outline-none focus:ring-2 focus:ring-blue-600/30"
              data-testid="catalog-custom-price-input" />
          </div>
        )}

        <div className="flex flex-col gap-2 pt-1">
          {canShare && detail.final_price != null && (
            <Button className="w-full fc-btn-primary rounded-xl h-11" onClick={share} data-testid="catalog-whatsapp-share-button">
              <Share2 className="w-4 h-4 mr-2" /> Share details on WhatsApp
            </Button>
          )}
          {canShare && cPhotos.length > 0 && (
            <Button variant="outline" className="w-full rounded-xl h-11" onClick={sharePhotos} data-testid="catalog-share-photos-button">
              <Share2 className="w-4 h-4 mr-2" /> Share {cPhotos.length} photo{cPhotos.length > 1 ? "s" : ""} on WhatsApp
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
